import { useEffect, useState } from "react";
import { X, Trash2, Loader2, Tag, Check, Ruler } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { api, sgd, resolveImage, preorderState } from "@/lib/api";
import SizeGuideDrawer from "@/components/SizeGuideDrawer";

// Label a cart-line piece selection: use type-specific labels unless the look has only one piece
const pieceLabel = (sel, isOnly) => {
  if (isOnly) return "Size";
  const t = (sel.piece_type || "").toLowerCase();
  if (t === "top") return "Top";
  if (t === "bottom") return "Bottom";
  if (t === "dress") return "Size";
  if (t === "outer") return "Outer";
  return "Size";
};
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, open, setOpen, remove, updateQty, updateSize, updateSelection, total, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [promo, setPromo] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount}
  const [promoLoading, setPromoLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [preorderAck, setPreorderAck] = useState(false);
  const [sgOpen, setSgOpen] = useState(false);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);
  useEffect(() => { api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, [open]);

  const po = preorderState(settings);

  // Save abandoned bag whenever email + items exist (debounced)
  useEffect(() => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    const t = setTimeout(() => {
      api.post("/bag/save", {
        email,
        subtotal: total,
        origin_url: window.location.origin,
        items: items.map(i => ({
          look_id: i.look_id, look_number: i.look_number, look_name: i.look_name, image: i.image,
          kind: i.kind, piece_id: i.piece_id, piece_name: i.piece_name,
          size: i.size, color: i.color, quantity: i.quantity, price: i.price,
          selections: i.selections || null,
        })),
      }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [email, items, total]);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promo.trim(), subtotal: total });
      setPromoApplied({ code: data.code, discount: data.discount });
      toast.success(`${data.code} applied · saved ${sgd(data.discount)}`);
    } catch (e) {
      setPromoApplied(null);
      toast.error(e.response?.data?.detail || "Invalid code");
    }
    setPromoLoading(false);
  };

  const removePromo = () => { setPromoApplied(null); setPromo(""); };

  const finalTotal = Math.max(0, total - (promoApplied?.discount || 0));

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        email: email || null,
        promo_code: promoApplied?.code || null,
        items: items.map(i => ({
          look_id: i.look_id, kind: i.kind, piece_id: i.piece_id,
          size: i.size, color: i.color, quantity: i.quantity,
          selections: i.selections ? i.selections.map(s => ({
            piece_id: s.piece_id, piece_type: s.piece_type, piece_name: s.piece_name,
            size: s.size, color: s.color,
          })) : undefined,
        })),
      };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout — please try again");
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/25" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[440px] flex flex-col" style={{ background: "#FDFBF7" }}>
        <div className="flex items-center justify-between px-6 py-5 border-b" style={{ borderColor: "#EBE5ED" }}>
          <div className="font-serif-display text-[20px] tracking-editorial">Your Bag ({count})</div>
          <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Close"><X size={20} strokeWidth={1.2} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {items.length === 0 && (
            <div className="text-center pt-24">
              <div className="font-serif-display text-[22px]">Your bag is empty</div>
              <p className="text-[13px] mt-3 text-[#4A464A]/70">Every look is already matched — go find yours.</p>
            </div>
          )}
          {items.map(i => (
            <div key={i.id} className="flex gap-4 border-b pb-6" style={{ borderColor: "#EBE5ED" }}>
              <img src={resolveImage(i.image)} alt={i.look_name} className="w-24 h-32 object-cover" />
              <div className="flex-1 text-[13px]">
                <div className="text-[10px] uppercase tracking-editorial text-[#C3B1C5]">LOOK {i.look_number}</div>
                <div className="font-serif-display text-[17px] mt-0.5">{i.look_name}</div>
                {i.kind === "complete" ? (
                  <div className="mt-2 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] uppercase tracking-editorial text-[#4A464A]/70">Complete Look</div>
                      <button type="button" onClick={() => setSgOpen(true)} data-testid={`cart-size-guide-${i.id}`} className="text-[11px] inline-flex items-center gap-1 border-b pb-[1px] text-[#4A464A]/70 hover:text-[#4A464A]" style={{ borderColor: "#C3B1C5" }}>
                        <Ruler size={11} strokeWidth={1.3} /> Size Guide
                      </button>
                    </div>
                    {(i.selections || []).map(sel => {
                      const isOnly = (i.selections || []).length === 1;
                      const label = pieceLabel(sel, isOnly);
                      return (
                        <div key={sel.piece_id} className="space-y-1.5">
                          {!isOnly && <div className="text-[11px] text-[#4A464A]/70">{sel.piece_name}</div>}
                          <div className="flex items-center gap-2 text-[12px] flex-wrap">
                            <span className="w-16 text-[#4A464A]/70">{label}</span>
                            {(sel.sizes || []).length > 0 ? (
                              <select data-testid={`cart-${sel.piece_type}-size-${i.id}`} value={sel.size || ""} onChange={e => updateSelection(i.id, sel.piece_id, { size: e.target.value })} className="border px-2 py-1 bg-transparent" style={{ borderColor: "#EBE5ED" }}>
                                {sel.sizes.map(s => <option key={s}>{s}</option>)}
                              </select>
                            ) : (
                              <span className="text-[11px] uppercase tracking-editorial text-[#4A464A]/50">—</span>
                            )}
                            {(sel.colors || []).length > 0 && (
                              <>
                                <span className="w-14 text-[#4A464A]/70 pl-1">Colour</span>
                                <select data-testid={`cart-${sel.piece_type}-color-${i.id}`} value={sel.color || ""} onChange={e => updateSelection(i.id, sel.piece_id, { color: e.target.value })} className="border px-2 py-1 bg-transparent" style={{ borderColor: "#EBE5ED" }}>
                                  {sel.colors.map(c => <option key={c}>{c}</option>)}
                                </select>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-2 space-y-1.5 text-[12px]">
                    <div className="flex items-center justify-between">
                      <span>{i.piece_name}</span>
                      <button type="button" onClick={() => setSgOpen(true)} data-testid={`cart-size-guide-piece-${i.id}`} className="text-[11px] inline-flex items-center gap-1 border-b pb-[1px] text-[#4A464A]/70 hover:text-[#4A464A]" style={{ borderColor: "#C3B1C5" }}>
                        <Ruler size={11} strokeWidth={1.3} /> Size Guide
                      </button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {(i.sizes || []).length > 0 && (
                        <>
                          <span className="text-[#4A464A]/70">Size</span>
                          <select data-testid={`cart-piece-size-${i.id}`} value={i.size || ""} onChange={e => updateSize(i.id, { size: e.target.value })} className="border px-2 py-1 bg-transparent" style={{ borderColor: "#EBE5ED" }}>
                            {i.sizes.map(s => <option key={s}>{s}</option>)}
                          </select>
                        </>
                      )}
                      {(i.colors || []).length > 0 && (
                        <>
                          <span className="text-[#4A464A]/70">Colour</span>
                          <select data-testid={`cart-piece-color-${i.id}`} value={i.color || ""} onChange={e => updateSize(i.id, { color: e.target.value })} className="border px-2 py-1 bg-transparent" style={{ borderColor: "#EBE5ED" }}>
                            {i.colors.map(c => <option key={c}>{c}</option>)}
                          </select>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border" style={{ borderColor: "#EBE5ED" }}>
                    <button onClick={() => updateQty(i.id, i.quantity - 1)} className="px-2 py-1">−</button>
                    <span className="px-3 text-[12px]">{i.quantity}</span>
                    <button onClick={() => updateQty(i.id, i.quantity + 1)} className="px-2 py-1">+</button>
                  </div>
                  <div className="text-[13px]">{sgd(i.price * i.quantity)}</div>
                </div>
              </div>
              <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`} className="text-[#4A464A]/50 hover:text-[#4A464A]"><Trash2 size={16} strokeWidth={1.2} /></button>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t px-6 py-5 space-y-4" style={{ borderColor: "#EBE5ED" }}>
            {/* promo */}
            {!promoApplied ? (
              <div className="flex items-center gap-2">
                <Tag size={14} strokeWidth={1.2} className="text-[#C3B1C5]" />
                <input data-testid="cart-promo" placeholder="Promo code" value={promo} onChange={e => setPromo(e.target.value.toUpperCase())} className="flex-1 border px-3 py-2 text-[12px] tracking-editorial bg-transparent" style={{ borderColor: "#EBE5ED" }} />
                <button onClick={applyPromo} disabled={promoLoading} data-testid="cart-apply-promo" className="text-[11px] uppercase tracking-editorial border px-3 py-2" style={{ borderColor: "#4A464A" }}>
                  {promoLoading ? "…" : "Apply"}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between text-[12px] px-3 py-2" style={{ background: "#EBE5ED" }} data-testid="cart-promo-applied">
                <span className="flex items-center gap-2"><Check size={13} /> {promoApplied.code} · −{sgd(promoApplied.discount)}</span>
                <button onClick={removePromo} className="text-[11px] underline">remove</button>
              </div>
            )}

            <input data-testid="cart-email" type="email" placeholder="Email for order confirmation" value={email} onChange={e => setEmail(e.target.value)} className="w-full border px-3 py-2 text-[13px] bg-transparent" style={{ borderColor: "#EBE5ED" }} />

            <div className="space-y-1 text-[13px]">
              <div className="flex justify-between text-[#4A464A]/70"><span>Subtotal</span><span>{sgd(total)}</span></div>
              {promoApplied && <div className="flex justify-between" style={{ color: "#AAB3A8" }}><span>Discount ({promoApplied.code})</span><span>−{sgd(promoApplied.discount)}</span></div>}
              <div className="flex justify-between uppercase tracking-editorial pt-2"><span>Total</span><span>{sgd(finalTotal)}</span></div>
            </div>

            {po.active && (
              <div className="text-[12px] px-3 py-2.5" style={{ background: "#EBE5ED", color: "#4A464A" }} data-testid="cart-preorder-note">
                All items are pre-order. Orders close <strong>{po.closeDateFmt}</strong> and ship from <strong>{po.shipDateFmt}</strong>.
              </div>
            )}
            {po.closed && (
              <div className="text-[12px] px-3 py-2.5" style={{ background: "#EBE5ED", color: "#4A464A" }} data-testid="cart-preorder-closed">
                Pre-orders have closed — the shop is back soon ♡
              </div>
            )}
            {po.active && (
              <label className="flex items-start gap-2 text-[12px] leading-snug cursor-pointer" data-testid="cart-preorder-ack-label">
                <input type="checkbox" checked={preorderAck} onChange={e => setPreorderAck(e.target.checked)} data-testid="cart-preorder-ack" className="mt-0.5" />
                <span>I understand this is a pre-order and my order will ship from <strong>{po.shipDateFmt}</strong>.</span>
              </label>
            )}

            <button onClick={checkout} disabled={loading || po.closed || (po.active && !preorderAck)} data-testid="cart-checkout" className="w-full py-3.5 text-[12px] uppercase tracking-editorial flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" style={{ background: "#4A464A", color: "#FDFBF7" }}>
              {loading && <Loader2 size={14} className="animate-spin" />}
              {po.closed ? "Pre-orders closed · back soon" : (po.active ? `Pre-order · ${sgd(finalTotal)}` : `Checkout · ${sgd(finalTotal)}`)}
            </button>
            <div className="flex items-center justify-center gap-3 text-[10px] uppercase tracking-editorial text-[#4A464A]/60" data-testid="cart-payment-methods">
              <span>Card</span>
              <span className="w-1 h-1 rounded-full" style={{ background: "#C3B1C5" }} />
              <span style={{ color: "#4A464A" }}>PayNow ✓</span>
              <span className="w-1 h-1 rounded-full" style={{ background: "#C3B1C5" }} />
              <span>SGD</span>
            </div>
            <p className="text-[10px] text-center tracking-editorial text-[#4A464A]/45">Secure Stripe checkout · Try WELCOME10</p>
          </div>
        )}
      </aside>
      <SizeGuideDrawer open={sgOpen} onClose={() => setSgOpen(false)} />
    </div>
  );
}
